import { useCallback, useEffect, useState } from 'react';
import { api, ApiFoutException, volgSessie, type ProjectSamenvatting } from './api/client';
import { Editor } from './components/Editor';
import { Inlogscherm } from './components/Inlogscherm';

/**
 * Het startscherm: projecten kiezen of een nieuw project beginnen.
 * Zodra een project open is, neemt de editor het over.
 */
export function App() {
  const [projecten, setProjecten] = useState<ProjectSamenvatting[]>([]);
  const [actiefProject, setActiefProject] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [laden, setLaden] = useState(true);
  const [poort, setPoort] = useState<'laden' | 'inloggen' | 'open'>('laden');
  const [loginVerplicht, setLoginVerplicht] = useState(false);

  const ververs = useCallback(async () => {
    setLaden(true);
    try {
      const resultaat = await api.projecten();
      setProjecten(resultaat.items);
      setFout(null);
    } catch (f) {
      setFout(
        f instanceof ApiFoutException
          ? `De server is niet bereikbaar of antwoordde met een fout: ${f.message}`
          : 'De server is niet bereikbaar.',
      );
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    volgSessie(() => setPoort('inloggen'));
  }, []);

  useEffect(() => {
    let geannuleerd = false;
    void (async () => {
      try {
        const sessie = await api.sessie();
        if (geannuleerd) return;
        setLoginVerplicht(sessie.verplicht);
        if (sessie.verplicht && !sessie.ingelogd) {
          setPoort('inloggen');
          setLaden(false);
          return;
        }
        setPoort('open');
        await ververs();
      } catch (f) {
        if (geannuleerd) return;
        setPoort('open');
        setFout(f instanceof Error ? f.message : 'De server is niet bereikbaar.');
        setLaden(false);
      }
    })();
    return () => {
      geannuleerd = true;
    };
  }, [ververs]);

  const uitloggen = async () => {
    try {
      await api.uitloggen();
    } catch {
      // De cookie wissen is het doel; een netwerkfout tonen we alsnog het scherm.
    }
    setActiefProject(null);
    setPoort('inloggen');
  };

  const maakProject = async (beeldformaat: string) => {
    setBezig(true);
    try {
      const dto = await api.maakProject({ naam: 'Naamloos project', beeldformaat });
      setActiefProject(dto.id);
    } catch (f) {
      setFout(f instanceof Error ? f.message : 'Project aanmaken mislukt.');
    } finally {
      setBezig(false);
    }
  };

  if (poort === 'laden') {
    return (
      <div className="laadscherm">
        <span>Laden…</span>
      </div>
    );
  }

  if (poort === 'inloggen') {
    return (
      <Inlogscherm
        onIngelogd={() => {
          setPoort('open');
          void ververs();
        }}
      />
    );
  }

  if (actiefProject) {
    return (
      <Editor
        projectId={actiefProject}
        onTerug={() => {
          setActiefProject(null);
          void ververs();
        }}
        onUitloggen={loginVerplicht ? () => void uitloggen() : undefined}
      />
    );
  }

  return (
    <div className="startscherm">
      <header className="startscherm__kop">
        <div>
          <h1>Video Studio</h1>
          <p className="startscherm__onder">
            Monteer korte video's, advertenties en projectpresentaties. Alles blijft op deze server.
          </p>
        </div>
        {loginVerplicht && (
          <button type="button" className="knop knop--stil" onClick={() => void uitloggen()}>
            Uitloggen
          </button>
        )}
      </header>

      {fout && (
        <div className="melding melding--fout" role="alert">
          {fout}
        </div>
      )}

      <section className="startscherm__sectie">
        <h2>Nieuw project</h2>
        <div className="startscherm__formaten">
          {[
            { id: '16:9', titel: 'Landscape 16:9', uitleg: 'YouTube, presentaties, website' },
            { id: '9:16', titel: 'Verticaal 9:16', uitleg: 'Reels, Shorts, TikTok' },
            { id: '1:1', titel: 'Vierkant 1:1', uitleg: 'Feed-posts' },
            { id: '4:5', titel: 'Portret 4:5', uitleg: 'Instagram-feed' },
          ].map((formaat) => (
            <button
              key={formaat.id}
              type="button"
              className="formaatkaart"
              disabled={bezig}
              onClick={() => void maakProject(formaat.id)}
            >
              <span className={`formaatkaart__vlak formaatkaart__vlak--${formaat.id.replace(':', '-')}`} />
              <strong>{formaat.titel}</strong>
              <span>{formaat.uitleg}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="startscherm__sectie">
        <h2>Klaar om te bekijken</h2>
        {projecten.some((project) => project.laatsteVideo) ? (
          <div className="startscherm__videos">
            {projecten
              .filter((project) => project.laatsteVideo)
              .map((project) => (
                <figure key={project.id} className="videokaart">
                  <video src={project.laatsteVideo?.url} controls playsInline preload="metadata" />
                  <figcaption>
                    <strong>{project.naam}</strong>
                    <button type="button" className="knop knop--klein" onClick={() => setActiefProject(project.id)}>
                      Open het project
                    </button>
                  </figcaption>
                </figure>
              ))}
          </div>
        ) : (
          <p className="startscherm__leeg">Een afgeronde export verschijnt hier, zodat je hem meteen kunt afspelen.</p>
        )}
      </section>

      <section className="startscherm__sectie">
        <div className="startscherm__sectiekop">
          <h2>Bestaande projecten</h2>
          <button type="button" className="knop knop--stil" onClick={() => void ververs()}>
            Vernieuwen
          </button>
        </div>
        {laden ? (
          <p className="startscherm__leeg">Laden…</p>
        ) : projecten.length === 0 ? (
          <p className="startscherm__leeg">
            Nog geen projecten. Kies hierboven een beeldformaat om te beginnen.
          </p>
        ) : (
          <ul className="projectlijst">
            {projecten.map((project) => (
              <li key={project.id}>
                <button
                  type="button"
                  className="projectlijst__item"
                  onClick={() => setActiefProject(project.id)}
                >
                  <span className="projectlijst__naam">{project.naam}</span>
                  <span className="projectlijst__meta">
                    {project.breedte}×{project.hoogte} · {project.fps} fps · {project.aantalItems} lagen · versie{' '}
                    {project.versie}
                  </span>
                  <span className="projectlijst__datum">
                    {new Date(project.gewijzigdOp).toLocaleString('nl-NL')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
